import {
	DestinationsFlightsSearchFormSubmitInterface,
	FlightsSearchFormSubmitInterface,
	PassengersFieldRestrictionsFlightsSearchFormInterface,
} from '@smartlinks/react-design-system/dist/src/lib/flights/shared/interfaces/flights-search-form.interface';
import { FlightsSearchAirportsInterface, FlightsSearchAirlinesInterface } from '../interfaces/flights-airports.interface';
import { FlightAirlinesTypes } from '@smartlinks/react-design-system/dist/src/lib/flights/shared/enums/flight-types.enum';
import { OptionSelect } from '@smartlinks/react-design-system/dist/src/shared/interfaces/inputs.interface';
import { CalendarRestrictionTypes } from '../enums/calendar-restrictions.enum';
import { Agency, CalendarRestrictions } from '../interfaces/agency.interface';
import { CommonNumberMagicEnum } from '../enums/common-number-magic.enum';
import { MapFlightsUrlUtil } from './map-flights-search-url.util';

export class FlightsUtil {
	public static mapCabins(traductions: (label: string) => string, agency: Agency): OptionSelect[] {
		const allowedCabins = agency.flightsCriteriaConfiguration?.allowedCabins;
		if (allowedCabins?.length) {
			let options: OptionSelect[] = [];
			const defaultCabin: string = 'economy';
			const priorizeCabin = allowedCabins.find(cabin => cabin === defaultCabin);
			let filterCabin = allowedCabins;

			if (priorizeCabin) {
				options = [
					{
						label: traductions(priorizeCabin),
						value: priorizeCabin,
					},
				];

				filterCabin = allowedCabins.filter(cabin => cabin !== defaultCabin);
			}

			filterCabin.forEach((cabin: string): void => {
				options.push({
					label: traductions(cabin),
					value: cabin,
				});
			});

			return [...options];
		}

		return [];
	}

	public static passengersRestrictions(agency?: Agency | null): PassengersFieldRestrictionsFlightsSearchFormInterface {
		return {
			maxPassengers: 9,
			minPassengers: agency?.flightsCriteriaConfiguration?.totalPassengersBySearch ?? CommonNumberMagicEnum.ONE,
			infants: {
				default: agency?.flightsCriteriaConfiguration?.infants?.default || 0,
				minimum: agency?.flightsCriteriaConfiguration?.infants?.minimum || 0,
				maximum: agency?.flightsCriteriaConfiguration?.infants?.maximum || 0,
			},
			children: {
				default: agency?.flightsCriteriaConfiguration?.children?.default || 0,
				minimum: agency?.flightsCriteriaConfiguration?.children?.minimum || 0,
				maximum: agency?.flightsCriteriaConfiguration?.children?.maximum || 0,
			},
			adults: {
				default: agency?.flightsCriteriaConfiguration?.adults?.default || 1,
				minimum: agency?.flightsCriteriaConfiguration?.adults?.minimum || 1,
				maximum: agency?.flightsCriteriaConfiguration?.adults?.maximum || 0,
			},
		};
	}

	public static calendarRestriction(agency?: Agency | null): number {
		return (
			agency?.calendarRestrictions?.find((restriction: CalendarRestrictions) => restriction.product === CalendarRestrictionTypes.flights)?.value || 0
		);
	}

	public static mapAirlinesOptions(traductions: (label: string) => string, airlines: FlightsSearchAirlinesInterface[]): OptionSelect[] {
		const airportsList: OptionSelect[] = [];
		airlines.forEach((airline: FlightsSearchAirlinesInterface) => {
			airportsList.push({
				label: airline.name,
				value: airline.code,
			});
		});

		airportsList.sort((a: OptionSelect, b: OptionSelect): number => {
			const aString = a.label.toLowerCase();
			const bString = b.label.toLowerCase();
			if (aString < bString) {
				return -1;
			}

			if (aString > bString) {
				return 1;
			}

			return 0;
		});

		airportsList.unshift({
			label: traductions('all'),
			value: FlightAirlinesTypes.all,
		});

		return [...airportsList];
	}

	public static getStatusNational(
		agency: Agency,
		airports: FlightsSearchAirportsInterface[],
		destinations: DestinationsFlightsSearchFormSubmitInterface[],
	): boolean {
		const countryCode: string = agency.profile.countryCode;
		const destinationInternational: DestinationsFlightsSearchFormSubmitInterface | undefined = destinations.find(
			(destination: DestinationsFlightsSearchFormSubmitInterface): boolean => {
				if (!destination.departure.airportCode) {
					return false;
				}

				const airport: FlightsSearchAirportsInterface | undefined = airports.find((airport: FlightsSearchAirportsInterface): boolean => {
					return (
						(airport.code === destination.departure.airportCode || airport.code === destination.arrival.airportCode) &&
						countryCode !== airport.countryCode
					);
				});

				return !(airport == null);
			},
		);

		return destinationInternational == null;
	}

	public static mapAngularRoute(data: FlightsSearchFormSubmitInterface, agencyCountryCode: string): string {
		return MapFlightsUrlUtil.mapAngular(data, agencyCountryCode);
	}
}
